export interface ApiResponse<T = any> {
  ok: boolean;
  payload: T | null;
  source?: "live" | "fallback";
  error: { message: string; code?: string } | null;
  meta: {
    timestamp: string;
    version?: string;
    env?: string;
  };
}

const createDefaultEnvelope = (errorMsg?: string, code = "UNKNOWN"): ApiResponse => ({
  ok: false,
  payload: null,
  error: { message: errorMsg || "Unknown Error", code },
  meta: { timestamp: new Date().toISOString(), version: '1.2.1' }
});

const safeFetch = async <T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> => {
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 8000); // 8 second fetch timeout
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    clearTimeout(id);
    
    const data = await response.json().catch(() => null);

    // If fetch failed or returned non-JSON
    if (!data || typeof data !== 'object') {
      return createDefaultEnvelope(`Server responded with status ${response.status}`, "HTTP_ERR");
    }

    // Ensure the response strictly follows the envelope, providing defaults for missing keys
    return {
      ok: data.ok === true,
      payload: data.payload !== undefined ? data.payload : null,
      source: data.source || (data.ok ? "live" : "unknown"),
      error: data.error || (data.ok ? null : { message: "Operation failed", code: "API_ERR" }),
      meta: data.meta || { timestamp: new Date().toISOString() }
    } as ApiResponse<T>;

  } catch (error: any) {
    const isTimeout = error.name === 'AbortError';
    console.error(`Fetch Failure [${url}]:`, error.message);
    return createDefaultEnvelope(
      isTimeout ? "Network request timed out" : (error.message || "Connection failed"),
      isTimeout ? "TIMEOUT" : "NETWORK_ERR"
    );
  }
};

export const checkApiHealth = async (): Promise<ApiResponse> => {
  return safeFetch('/api/health');
};

export const fetchContentPlan = async (params: any): Promise<ApiResponse> => {
  return safeFetch('/api/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
};

export { safeFetch };